import { Q } from "@nozbe/watermelondb";
import {
  withObservables,
  ExtractedObservables,
  useDatabase,
} from "@nozbe/watermelondb/react";
import { Card, FlatActionList } from "@towersystems/roam-common-native";
import { useMemo } from "react";
import {
  DEV_TYRO_TYPE_ID,
  LINKLY_TYPE_ID,
  TYRO_TYPE_ID,
} from "../../../../../../constants";
import PaymentMethod from "../../../../../../utilities/data/model/PaymentMethod";
import { useTranslation } from "../../../../../../utilities/i18n";
import { SettingsLandingScreenProps } from "../../SettingsLandingScreen";
import Terminal from "../../../../../../utilities/data/model/Terminal";
import React from "react";
import { useFocusEffect } from "@react-navigation/native";

const getObservables = ({ terminal }: { terminal: Terminal }) => {
  return {
    terminal: terminal.observe(),
  };
};

export interface PaymentMethodSettingListCardRenderProps
  extends ExtractedObservables<ReturnType<typeof getObservables>> {
  navigation: SettingsLandingScreenProps["navigation"];
}

export const PaymentMethodSettingListCardRender = ({
  navigation,
  terminal,
}: PaymentMethodSettingListCardRenderProps) => {
  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethod[]>(
    []
  );
  const database = useDatabase();

  useFocusEffect(
    React.useCallback(() => {
      const fetchPaymentMethods = async () => {
        const methods = await database
          .get<PaymentMethod>("payment_methods")
          .query(
            Q.where("enabled", true),
            Q.on("pay_types", "internal", Q.eq(false)),
            Q.where("deleted_at", Q.eq(null))
          )
          .fetch();
        setPaymentMethods(methods);
      };
      fetchPaymentMethods().catch(console.error);
    }, [])
  );

  const { t } = useTranslation("settings");

  const paymentMethodIds = terminal.paymentMethodIds || [];

  const showTyro = useMemo(() => {
    const matchingPaymentMethods = paymentMethods.filter((i) =>
      paymentMethodIds.includes(i.id)
    );
    return Boolean(
      matchingPaymentMethods.find((i) => i.payType.id === TYRO_TYPE_ID)
    );
  }, [paymentMethodIds, paymentMethods]);

  const showTyroDev = useMemo(() => {
    const matchingPaymentMethods = paymentMethods.filter((i) =>
      paymentMethodIds.includes(i.id)
    );
    return Boolean(
      matchingPaymentMethods.find((i) => i.payType.id === DEV_TYRO_TYPE_ID)
    );
  }, [paymentMethodIds, paymentMethods]);

  const linklyPaymentMethods = useMemo(() => {
    const matchingPaymentMethods = paymentMethods.filter((i) =>
      paymentMethodIds.includes(i.id)
    );
    return matchingPaymentMethods.filter(
      (i) => i.payType.id === LINKLY_TYPE_ID
    );
  }, [[paymentMethods]]);

  const showLinkly = linklyPaymentMethods.length > 0;

  const smartPayPaymentMethods = useMemo(() => {
    const matchingPaymentMethods = paymentMethods.filter((i) =>
      paymentMethodIds.includes(i.id)
    );

    return matchingPaymentMethods.filter((i) => i.payType.id === "40");
  }, [paymentMethods]);

  const towerPaymentsMethods = useMemo(() => {
    const matchingPaymentMethods = paymentMethods.filter((i) =>
      paymentMethodIds.includes(i.id)
    );

    return matchingPaymentMethods.filter((i) => i.payType.id === "45");
  }, [paymentMethods]);

  const items = [];

  if (showTyro) {
    items.push({
      key: "tyro",
      title: t("action.tyro.label"),
      subTitle: t("action.tyro.description"),

      onPress: () =>
        navigation.navigate("TyroSettingsLandingScreen", {
          isDev: false,
        }),
    });
  }

  if (showTyroDev) {
    items.push({
      key: "tyro_dev",
      title: t("action.tyro_dev.label"),
      subTitle: t("action.tyro_dev.description"),
      onPress: () =>
        navigation.navigate("TyroSettingsLandingScreen", {
          isDev: true,
        }),
    });
  }

  if (showLinkly) {
    for (const paymentMethod of linklyPaymentMethods) {
      items.push({
        key: "linkly",
        title: t("action.linkly.label", {
          payment_method_name: paymentMethod.name,
        }),
        subTitle: t("action.linkly.description"),
        onPress: () =>
          navigation.navigate("LinklySettingsLandingScreen", {
            paymentMethodId: paymentMethod.id,
          }),
      });
    }
  }

  if (towerPaymentsMethods.length > 0) {
    for (const paymentMethod of towerPaymentsMethods) {
      items.push({
        key: "tower_payments",
        title: `${t("action.tower_payments.label")} - ${paymentMethod.name}`,
        subTitle: t("action.tower_payments.description"),
        onPress: () =>
          navigation.navigate("ManageTowerPaymentsRootScreen", {
            screen: "ManageTowerPaymentsLandingScreen",
            paymentMethodId: paymentMethod.id,
          }),
      });
    }
  }

  if (smartPayPaymentMethods.length > 0) {
    for (const paymentMethod of smartPayPaymentMethods) {
      items.push({
        key: `smartpay-${paymentMethod.id}`,
        title: `Manage SmartPay - ${paymentMethod.name}`,
        subTitle: t("action.smartpay.description"),
        onPress: () =>
          navigation.navigate("ManageSmartPayRootScreen", {
            screen: "ManageSmartPayScreen",
            paymentMethodId: paymentMethod.id,
          }),
      });
    }
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <Card title={t("card.payment_method.title")}>
      <FlatActionList items={items} />
    </Card>
  );
};

const enhance = withObservables(["terminal"], getObservables);

export const PaymentMethodSettingListCard = enhance(
  PaymentMethodSettingListCardRender
);
